import {Suspense, useState} from 'react'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Button, IconButton} from '@primer/react'
import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {ActivityEmpty} from './activity/ActivityEmpty'
import {ActivityError} from './activity/ActivityError'
import {ActionItemsLoading} from './action-items/ActionItemsLoading'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {pullRequestsLayoutRoute} from '../../routes/layout-route'
import type {ActivityPayload} from '../../routes/route-payload-types'
import {buildEventGroups} from '../../helpers/catch-up-helpers'
import {EventList} from './ActivityEventList'
import styles from './Activity.module.css'
import type {NavigationUrls} from '../../types/navigation-urls-types'
import {Link} from '@github-ui/react-core/link'
import {useSuspenseActivityPageData} from './data/use-activity-page-data'

type ActivityProps = {
  sinceTimestamp: string
  lastActivityTimestamp: string | null
}

/** Activity section showing recent timeline updates since last engagement */
export function Activity({sinceTimestamp, lastActivityTimestamp}: ActivityProps) {
  if (!sinceTimestamp) return null

  return (
    <ErrorBoundary boundaryName="Activity" fallback={<ActivityError />}>
      <Suspense key={sinceTimestamp} fallback={<ActionItemsLoading />}>
        <ActivityContent sinceTimestamp={sinceTimestamp} lastActivityTimestamp={lastActivityTimestamp} />
      </Suspense>
    </ErrorBoundary>
  )
}

function ActivityContent({
  sinceTimestamp,
  lastActivityTimestamp,
}: {
  sinceTimestamp: string
  lastActivityTimestamp: string | null
}) {
  const {data: layoutData} = useRouteQuery(pullRequestsLayoutRoute, 'mainQuery')
  const {data: activityData} = useSuspenseActivityPageData({sinceTimestamp, lastActivityTimestamp})

  return <ActivityComponent activityData={activityData} urls={layoutData.urls} />
}

function ActivityComponent({activityData, urls}: {activityData: ActivityPayload; urls: NavigationUrls}) {
  const {changes: changesUrl, conversation: conversationUrl} = urls
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set())

  const summary = activityData.summary
  const items = buildEventGroups(summary, changesUrl)

  const toggleGroup = (type: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev)
      if (next.has(type)) {
        next.delete(type)
      } else {
        next.add(type)
      }
      return next
    })
  }

  if (items.length === 0) {
    return <ActivityEmpty />
  }

  return (
    <div className={styles.activityContainer}>
      {items.map(item => {
        const isExpanded = expandedGroups.has(item.type)
        return (
          <div key={`catch-up-item-${item.type}`} className={styles.activityItem}>
            <div className={styles.activityHeader}>
              {item.count > 0 && (
                <IconButton
                  aria-expanded={isExpanded}
                  aria-label={`${isExpanded ? 'Hide' : 'Show'} details for ${item.label.toLocaleLowerCase()}`}
                  size="small"
                  variant="invisible"
                  onClick={() => toggleGroup(item.type)}
                  icon={isExpanded ? ChevronDownIcon : ChevronRightIcon}
                />
              )}
              <span className={styles.activityLabel}>{item.label}</span>
              {item.link && (
                <Button
                  as={Link}
                  to={item.link}
                  size="small"
                  variant="invisible"
                  aria-label={`View ${item.label.toLowerCase()} in Files tab`}
                  className={styles.activityButton}
                >
                  View changes
                </Button>
              )}
            </div>
            {isExpanded && (
              <div className={styles.activityDetails}>
                <EventList
                  events={item.events}
                  type={item.type}
                  changesUrl={changesUrl}
                  conversationUrl={conversationUrl}
                />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
